import {
  BasePagination,
  Checkbox,
  formatPrice,
  Heading,
  HStack,
  IconButtonWithTooltip,
  PlusIcon,
  VStack,
  WhiteCard,
} from '@elkaso-app/web-design';
import { useGetCatalogItemUnitsApi } from 'apis/restaurants/warehouse-items/use-get-catalog-item-units';
import { useGetSupplierItemsApi } from 'apis/supplier/supplier-items/use-get-supplier-items-api';
import { useGetSupplierByIdApi } from 'apis/supplier/use-get-supplier-by-id-api';
import { BaseTable, BaseTableContainer, useCustomTableCheckboxes } from 'components/base-table';
import {
  BaseTableSpinner,
  BaseTableTitle,
  ClearFilterButton,
  CustomTableCell,
  TableCell,
  TableHeader,
} from 'components/base-table/components';
import { ActivationFilterButton, NameFilterButton } from 'components/base-table/filters';
import { BrandFilterButton } from 'components/base-table/filters/brand';
import { CodeFilterButton } from 'components/base-table/filters/code';
import { PriceFilterButton } from 'components/base-table/filters/price';
import { UnitNameFilterButton } from 'components/base-table/filters/unit-name';
import { BaseSortButton } from 'components/base-table/sorting';
import { setHiddenColumns } from 'components/base-table/utils';
import { TruncateText, TruncateTextProps } from 'components/truncate-text';
import { Can } from 'configs/can';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import { AddItemModalCell } from 'pages/suppliers/components/supplier-items/add-item-modal';
import { DownloadCSVDemoFile } from 'pages/suppliers/components/supplier-items/download-csv-demo-file';
import { DownloadProductCSVFile } from 'pages/suppliers/components/supplier-items/download-product-csv-file';
import { IItemStatusCell, ItemStatusCell } from 'pages/suppliers/components/supplier-items/item-status-cell';
import { ItemSynonymsCell, ItemSynonymsCellProps } from 'pages/suppliers/components/supplier-items/items-synonyms-cell';
import { OpenMappingSheet } from 'pages/suppliers/components/supplier-items/open-mapping-sheet';
import {
  IProductListActionsCell,
  ProductListActionsCell,
} from 'pages/suppliers/components/supplier-items/product-actions-cell';
import {
  IProductCategoryCell,
  ProductCategoryCell,
} from 'pages/suppliers/components/supplier-items/product-category-cell';
import { IProductImageCell, ProductImageCell } from 'pages/suppliers/components/supplier-items/product-image-cell';
import { RemoveItemsButton } from 'pages/suppliers/components/supplier-items/remove-items-button';
import { UploadCSVFiles } from 'pages/suppliers/components/supplier-items/upload-csv-files';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { CellProps } from 'react-table';
import { ActivateDeactivateItemsButton } from '../components/supplier-items/activate-deactivate-items-button';

export const SupplierProductsScreen = () => {
  const setPaginationParams = useSetPaginationParamsV1();
  const { supplierId } = useParams<{ supplierId: string }>();

  // Get supplier details
  const { data: supplierData } = useGetSupplierByIdApi(supplierId);

  // Get supplier items
  const { isLoading, isFetching, data } = useGetSupplierItemsApi();
  const { pageCount, total, data: itemsData } = data ?? {};

  // Get Units *Used in units filter & edit/add new item modal
  useGetCatalogItemUnitsApi();

  // Handle select table rows
  const { checkedItems, isIndeterminate, allChecked, setCheckboxState, handleParentChange, handleChildChange } =
    useCustomTableCheckboxes(itemsData);

  const columns = useMemo(
    () => [
      {
        Header: <TableHeader name='ID' />,
        accessor: 'id',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['id']>) => <TableCell value={value} />,
      },
      {
        Header: <TableHeader name='Image' />,
        accessor: 'image',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['image']>) => (
          <CustomTableCell<IProductImageCell> as={ProductImageCell} imageURL={value} />
        ),
      },
      {
        Header: <TableHeader name='Name(EN)' filterButtonComponent={<NameFilterButton />} />,
        accessor: 'nameEn',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['nameEn']>) => <TableCell value={value} />,
      },
      {
        Header: <TableHeader name='Name(AR)' filterButtonComponent={<NameFilterButton />} />,
        accessor: 'nameAr',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['nameAr']>) => <TableCell value={value} />,
      },
      {
        Header: <TableHeader name='Brand' filterButtonComponent={<BrandFilterButton />} />,
        accessor: 'brand',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['brand']>) => <TableCell value={value} />,
      },
      {
        Header: <TableHeader name='Categories' />,
        accessor: 'categories',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['categories']>) => (
          <CustomTableCell<IProductCategoryCell> as={ProductCategoryCell} categoryName={value} />
        ),
      },
      {
        Header: (
          <TableHeader
            name='Code'
            sortButtonComponent={<BaseSortButton sortBy='code' />}
            filterButtonComponent={<CodeFilterButton />}
          />
        ),
        accessor: 'code',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['code']>) => <TableCell value={value} />,
      },
      {
        Header: (
          <TableHeader
            name='Price'
            sortButtonComponent={<BaseSortButton sortBy='price' />}
            filterButtonComponent={<PriceFilterButton />}
          />
        ),
        accessor: 'price',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['price']>) => <TableCell value={formatPrice(value)} />,
      },
      {
        Header: (
          <TableHeader
            name='Unit'
            filterButtonComponent={<UnitNameFilterButton />}
            sortButtonComponent={<BaseSortButton sortBy='unit' />}
          />
        ),
        accessor: 'unit',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['unit']>) => <TableCell value={value} />,
      },
      {
        Header: <TableHeader name='Description' />,
        accessor: 'description',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['description']>) => (
          <CustomTableCell<TruncateTextProps> as={TruncateText} text={value} />
        ),
      },
      {
        Header: <TableHeader name='Synonyms' />,
        accessor: 'synonyms',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['synonyms']>) => (
          <CustomTableCell<ItemSynonymsCellProps> as={ItemSynonymsCell} value={value} />
        ),
      },
      {
        Header: (
          <TableHeader
            name='Status'
            sortButtonComponent={<BaseSortButton sortBy='isActive' />}
            filterButtonComponent={<ActivationFilterButton />}
          />
        ),
        accessor: 'status',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['status']>) => (
          <CustomTableCell<IItemStatusCell> as={ItemStatusCell} itemId={value?.itemId} isActive={value?.isActive} />
        ),
      },
      {
        Header: <TableHeader name='Actions' />,
        accessor: 'actions',
        Cell: ({ row, value }: CellProps<any, IColumnsAccessorData['actions']>) => (
          <CustomTableCell<IProductListActionsCell>
            as={ProductListActionsCell}
            row={row}
            item={value}
            isSelected={checkedItems[row.index]?.isChecked}
            onSelectChange={handleChildChange}
          />
        ),
      },
    ],
    [checkedItems]
  );

  interface IColumnsAccessorData {
    id: number;
    image: string;
    nameEn: string;
    nameAr: string;
    brand: string;
    categories: string;
    code: string;
    price: number;
    unit: string;
    description: string;
    synonyms: string[];
    status: {
      itemId: string;
      isActive: boolean;
    };
    actions: { [key: string]: any };
  }

  const columnsAccessorData = useMemo(
    () =>
      itemsData?.map((item: (typeof itemsData)[0]): IColumnsAccessorData => {
        return {
          id: item?.id,
          image: item?.images?.[0],
          nameEn: item?.name?.en,
          nameAr: item?.name?.ar,
          brand: item?.brand?.en,
          categories: item?.category?.tree?.find((item: any) => item.depth === 1)?.title,
          code: item?.code,
          price: item?.price,
          unit: item?.unit?.abbreviation,
          description: item?.description,
          synonyms: item?.synonyms,
          status: {
            itemId: item?.id,
            isActive: item?.isActive,
          },
          actions: item,
        };
      }),
    [itemsData]
  );

  return (
    <VStack spacing='sm' align='stretch'>
      <WhiteCard p='md'>
        <VStack spacing='md' align='start'>
          <Heading as='h3' fontSize='lg'>
            Upload Supplier Products From CSV File
          </Heading>

          <DownloadCSVDemoFile />
          <OpenMappingSheet />
          <UploadCSVFiles />
        </VStack>
      </WhiteCard>

      <BaseTableContainer isLoading={isLoading}>
        <BaseTableContainer.Header>
          <BaseTableContainer.Header.Left>
            <VStack spacing='sm' align='start'>
              <HStack>
                <BaseTableTitle title={`${supplierData?.data?.name}`} />
                <BaseTableSpinner isFetching={isFetching} />
              </HStack>

              <DownloadProductCSVFile />
            </VStack>
          </BaseTableContainer.Header.Left>

          <BaseTableContainer.Header.Right>
            <ClearFilterButton />

            <Can I='update' a='User'>
              <AddItemModalCell
                variant='add'
                trigger={<IconButtonWithTooltip label='Add item' aria-label='add-item-btn' Icon={<PlusIcon />} />}
              />
            </Can>
            <Can I='update' a='User'>
              <RemoveItemsButton checkedItems={checkedItems} />
            </Can>
            <Can I='update' a='User'>
              <ActivateDeactivateItemsButton checkedItems={checkedItems} items={itemsData} />
            </Can>

            <Can I='update' a='User'>
              <IconButtonWithTooltip
                label='Select all'
                aria-label='select-btn'
                isDisabled={!itemsData?.length}
                onClick={() => {
                  handleParentChange(!allChecked);
                  setCheckboxState(!allChecked);
                }}
                Icon={
                  <Checkbox
                    mb='unset'
                    colorScheme='gray'
                    borderColor='#757575'
                    isChecked={allChecked}
                    isIndeterminate={isIndeterminate}
                    pointerEvents='none'
                  />
                }
              />
            </Can>
          </BaseTableContainer.Header.Right>
        </BaseTableContainer.Header>

        <BaseTableContainer.TableWrapper>
          <BaseTable
            columns={columns}
            data={columnsAccessorData}
            initialHiddenColumns={setHiddenColumns(['actions'])}
          />
        </BaseTableContainer.TableWrapper>

        <BaseTableContainer.Footer>
          <BaseTableContainer.Footer.Left />
          <BaseTableContainer.Footer.Right>
            <BasePagination>
              <BasePagination.Pages pagesCount={pageCount} setSearchParams={setPaginationParams} />
              <BasePagination.Limit total={total} setSearchParams={setPaginationParams} />
            </BasePagination>
          </BaseTableContainer.Footer.Right>
        </BaseTableContainer.Footer>
      </BaseTableContainer>
    </VStack>
  );
};
